<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { Swiper as SwiperClass } from 'swiper/types';
import { useBreakpoints } from '@vueuse/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LandingTitleLayout from '../LandingTitleLayout.vue';
import type { Step } from '../../types';
import { breakpoints } from '@glow/shared/lib/breakpoints';
import type { LinkWithTitle } from '@glow/shared/utils/types';

const props = withDefaults(
  defineProps<{
    steps?: Step[];
    offerButton?: LinkWithTitle;
  }>(),
  { steps: () => [], offerButton: undefined }
);

const isMobile = useBreakpoints(breakpoints).smaller('md');
const currentStep = ref(1);
const isOfferButtonShow = ref(false);

const slider = ref<SwiperClass>();
function setSwiper(swiper: SwiperClass) {
  slider.value = swiper;
}
function onSlideChange() {
  if (!slider.value) return;
  currentStep.value = slider.value.realIndex + 1;
}

onMounted(() => {
  if (isMobile.value) return;

  gsap.registerPlugin(ScrollTrigger);
  gsap.set('.scene', { autoAlpha: 1 });

  const timeline = gsap.timeline({
    defaults: {
      overwrite: 'auto',
      ease: 'power1',
    },
    scrollTrigger: {
      end: '+=500%',
      pin: true,
      scrub: true,
      start: 'center center',
      trigger: '.scene',
    },
  });

  const stagger = 0.8;
  const duration = 0.6;

  gsap.set('.scene', { autoAlpha: 1 });

  timeline.set('.scroll-item', { transformOrigin: 'center bottom' });

  timeline
    .from('.scroll-item', {
      duration,
      opacity: 0,
      rotationX: 0,
      rotationY: 0,
      scale: 1,
      stagger,
      yPercent: 60,
      onUpdate() {
        const x = (this.progress() * 10) / (10 / props.steps.length);
        currentStep.value = Math.round(x);

        isOfferButtonShow.value = this.progress() === 1;
      },
    })
    .to(
      '.scroll-item',
      {
        duration,
        opacity: 0,
        rotationX: 20,
        rotationY: 2,
        scale: 0.5,
        stagger,
        y: -200,
      },
      stagger
    );
});
</script>

<template>
  <div id="how">
    <div class="container">
      <LandingTitleLayout>
        <template #title>
          <slot name="title" />
        </template>
      </LandingTitleLayout>
    </div>

    <div class="scene">
      <div class="scene__grid container d-none d-md-block">
        <div class="steps-wrapper">
          <div
            :class="[
              'steps mb-5',
              { 'steps_scale-down': props.steps.length > 5 },
            ]"
          >
            <div class="bar">
              <div
                v-for="index in props.steps.length"
                :key="index"
                :class="['point', { filled: currentStep >= index }]"
              ></div>
            </div>
            <div class="text">
              <div v-for="(step, index) in props.steps" :key="index">
                {{ step.title }}
              </div>
            </div>
          </div>

          <div
            v-if="props.offerButton && currentStep === props.steps.length"
            :class="[
              'offer-button',
              'offer-button_animate',
              { 'offer-button_animate_popup': isOfferButtonShow },
            ]"
          >
            <AppButton color="light" fluid :to="props.offerButton.link">
              <span class="button-text">{{ props.offerButton.title }}</span>
            </AppButton>
          </div>
        </div>

        <div
          v-for="(item, itemIndex) in props.steps"
          :key="itemIndex"
          class="scroll-item scheme"
        >
          <img :src="item.screenImageUrl" />
          <div
            v-for="(tip, index) in item.tips"
            :key="index"
            :class="[
              'speech-bubble',
              { large: tip.text.length > 60 },
              { popup: currentStep === itemIndex + 1 },
            ]"
            :style="[`top: ${tip.top};`, `left: ${tip.left}`]"
          >
            <span>{{ tip.text }}</span>
          </div>
        </div>
      </div>
      <Swiper
        :follow-finger="true"
        :slides-per-view="1"
        :space-between="8"
        class="d-md-none slider"
        @swiper="setSwiper"
        @slide-change="onSlideChange"
      >
        <SwiperSlide
          v-for="(item, itemIndex) in props.steps"
          :key="itemIndex"
          class="slide"
        >
          <div class="container slide-content">
            <div class="slide-text">
              <div class="title">{{ item.title }}</div>
              <div class="d-sm-none description">{{ item.subtitle }}</div>
              <div
                v-if="props.offerButton && itemIndex === props.steps.length - 1"
                class="offer-button"
              >
                <AppButton color="light" fluid :to="props.offerButton.link">
                  <span class="button-text">
                    {{ props.offerButton.title }}
                  </span>
                </AppButton>
              </div>
            </div>
            <div class="scheme">
              <img :src="item.screenImageUrl" />
              <div
                v-for="(tip, index) in item.tips"
                :key="index"
                :class="[
                  'speech-bubble',
                  { large: tip.text.length > 60 },
                  { popup: currentStep === itemIndex + 1 },
                ]"
                :style="[`top: ${tip.top};`, `left: ${tip.left}`]"
              >
                <span>{{ tip.text }}</span>
              </div>
            </div>
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$mobile-slide-height: 600px;
$mobile-landscape-height: 550px;

.scene {
  position: relative;
  width: 100%;
  height: 100vh;
  background: var(--app-gradient-primary);

  &__grid {
    height: 100%;

    @include media-breakpoint-up(lg) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @include media-breakpoint-down(md) {
    height: $mobile-landscape-height;
  }

  @include media-breakpoint-down(sm) {
    height: $mobile-slide-height;
    background: none;
  }

  .steps-wrapper {
    height: 100%;
    display: flex;
    align-items: flex-start;
    position: relative;

    @include media-breakpoint-up(lg) {
      align-items: center;
    }
  }

  .steps {
    display: flex;
    gap: 16px;
    align-items: center;

    @include media-breakpoint-down(lg) {
      margin-top: 150px;
    }

    .bar {
      display: flex;
      flex-direction: column;
      gap: 45px;

      .point {
        position: relative;
        width: 12px;
        height: 12px;
        border: 2px solid #fff;
        border-radius: 50%;

        &:not(:last-child):after {
          content: '';
          width: 2px;
          height: 45px;
          position: absolute;
          left: 3px;
          bottom: -47px;
          background: #fff;
        }

        &.filled {
          background: #fff;
        }
      }
    }

    .text {
      display: flex;
      flex-direction: column;
      gap: 28px;
      font-size: 24px;
      line-height: 28px;
      color: var(--app-color-white-secondary);
    }

    &_scale-down {
      transform: scale(0.8);

      @include media-breakpoint-down(lg) {
        margin-top: 100px;
      }
    }
  }
}

.slider {
  width: 100%;
  height: 100%;

  @include media-breakpoint-down(sm) {
    padding: 0 20px;
  }

  .slide {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;

    @include media-breakpoint-down(sm) {
      border-radius: 16px;
      background: var(--app-gradient-primary);
    }

    .slide-content {
      height: $mobile-slide-height;
      padding-top: 16px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;

      @include media-breakpoint-up(sm) {
        height: $mobile-landscape-height;
      }
    }
    .slide-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .title,
    .description {
      text-align: center;
      font-size: 17px;
      line-height: 24px;
      color: var(--app-color-white-secondary);
    }

    .title {
      font-weight: 600;
      line-height: 22px;

      @include media-breakpoint-only(sm) {
        font-size: 24px;
        line-height: 28px;
      }
    }
  }
}

.scheme {
  position: relative;
  width: 439px;

  padding: 10px 10px 0 10px;
  background: #fff;
  border-radius: 24px 24px 0 0;
  box-shadow:
    1.1125px 1.1125px 3.33749px rgba(80, 88, 113, 0.25),
    4.44999px 4.44999px 11.125px rgba(80, 88, 113, 0.08),
    inset 3.33749px 3.33749px 6.67499px rgba(121, 135, 176, 0.08),
    inset -4.44999px -4.44999px 16.6875px rgba(121, 135, 176, 0.25);

  @include media-breakpoint-down(xxl) {
    width: 339px;
  }

  @include media-breakpoint-up(md) {
    position: absolute;
    right: 50px;
    bottom: 0;
    transform: translate(-50%, 0%);
  }

  @include media-breakpoint-down(md) {
    padding: 8px 8px 0 8px;
    box-shadow:
      0.476385px 0.476385px 1.42916px rgba(80, 88, 113, 0.25),
      1.90554px 1.90554px 4.76385px rgba(80, 88, 113, 0.08),
      inset 1.42916px 1.42916px 2.85831px rgba(121, 135, 176, 0.08),
      inset -1.90554px -1.90554px 7.14578px rgba(121, 135, 176, 0.25);
    border-radius: 16px 16px 0 0;
  }

  @include media-breakpoint-down(sm) {
    width: 100%;
  }

  img {
    width: 100%;
    border-radius: 24px 24px 0 0;

    @include media-breakpoint-down(md) {
      border-radius: 16px 16px 0 0;
    }
  }

  .speech-bubble {
    display: none;
    width: 0;
    padding: 16px;
    font-size: 16px;
    line-height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 212 73' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 72.4911C1.99467 71.1262 3.16418 69.4495 3.5597 65.901V16C3.5597 7.16344 10.7231 0 19.5597 0H196C204.837 0 212 7.16345 212 16V56.9981C212 65.8346 204.837 72.9981 196 72.9981H15.0299C11.7693 73.0469 10.4585 72.1785 9.03408 69.5761C8.8674 69.2716 8.44945 69.2117 8.20516 69.4584C5.41369 72.2766 3.46924 72.7803 0 72.4911Z' fill='white'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    background-position: 0 0;
    background-size: 100% 100%;
    filter: drop-shadow(8px 16px 60px rgba(0, 0, 0, 0.24));
    position: absolute;
    overflow: hidden;
    transition: all ease 0.8s;

    span {
      visibility: hidden;
      opacity: 0;
      transition: all ease 2.8s;
    }

    &.popup {
      width: 212px;

      span {
        visibility: visible;
        opacity: 1;
      }

      &.large {
        width: 270px;
      }
    }

    @include media-breakpoint-up(sm) {
      display: block;
    }
  }
}

.offer-button {
  width: 300px;

  @include media-breakpoint-only(sm) {
    margin: 0 auto;
  }

  .button-text {
    font-weight: 600;
    color: var(--app-color-primary);
  }

  &_animate {
    width: 400px;
    position: absolute;
    left: 0;
    bottom: -100px;
    visibility: hidden;
    opacity: 0;
    transition: all 0.5s ease;

    .button-text {
      font-size: 24px;
      padding: 1.2em;
    }

    &_popup {
      visibility: visible;
      opacity: 1;
      bottom: 10%;

      @include media-breakpoint-down(xxl) {
        bottom: 5%;
      }

      @include media-breakpoint-down(xl) {
        bottom: 15%;
      }

      @include media-breakpoint-down(lg) {
        bottom: 50%;
      }
    }
  }
}
</style>
